/**
 * DiaEx mobile icon set — 24px outline glyphs, stroke 1.8, SF-Symbols
 * vibe. Every icon takes the same props so screens can restyle via
 * `className` (color inherits from currentColor) and resize via `size`.
 *
 * Usage: <IconHome size={24} className="text-ink" />
 */

export interface IconProps {
  /** Rendered width/height in px. Defaults to 24. */
  size?: number
  className?: string
  /** Stroke width override — chrome uses the 1.8 default. */
  strokeWidth?: number
}

function Svg({
  size = 24,
  className,
  strokeWidth = 1.8,
  children,
  filled = false,
}: IconProps & { children: React.ReactNode; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  )
}

export function IconHome(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 10.2 12 3.5l8.5 6.7" />
      <path d="M5.5 8.8V20a.8.8 0 0 0 .8.8h3.9v-5.6a1.8 1.8 0 0 1 3.6 0v5.6h3.9a.8.8 0 0 0 .8-.8V8.8" />
    </Svg>
  )
}

export function IconBrowse(p: IconProps) {
  // Diamond-grid: four rounded tiles, one tilted — browse/catalog.
  return (
    <Svg {...p}>
      <rect x="3.5" y="3.5" width="7.4" height="7.4" rx="2" />
      <rect x="13.1" y="3.5" width="7.4" height="7.4" rx="2" />
      <rect x="3.5" y="13.1" width="7.4" height="7.4" rx="2" />
      <rect x="13.1" y="13.1" width="7.4" height="7.4" rx="2" />
    </Svg>
  )
}

export function IconSearch(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.6 15.6 4.9 4.9" />
    </Svg>
  )
}

export function IconGemma(p: IconProps) {
  // Faceted diamond glyph — the Gemma brand mark. Also the Gemma bottom
  // tab (an equal 4th tab as of 2026-09-21 — gold-tinted when active,
  // soft when idle, like every other tab).
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 1.6}>
      <path d="M7 3.5h10l4 5.5-9 11.5L3 9l4-5.5Z" />
      <path d="M3 9h18" />
      <path d="M7 3.5 9.5 9 12 20.5 14.5 9 17 3.5" />
    </Svg>
  )
}

export function IconOrders(p: IconProps) {
  // Receipt with a zig-zag foot.
  return (
    <Svg {...p}>
      <path d="M6 3.5h12a.5.5 0 0 1 .5.5v16.5l-2.5-1.6-2 1.6-2-1.6-2 1.6-2-1.6-2.5 1.6V4a.5.5 0 0 1 .5-.5Z" />
      <path d="M8.6 8h6.8M8.6 11.5h6.8" />
    </Svg>
  )
}

export function IconProfile(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20.2c.9-3.6 3.8-5.5 7.2-5.5s6.3 1.9 7.2 5.5" />
    </Svg>
  )
}

export function IconBack(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 2}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Svg>
  )
}

export function IconChevronRight(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 2}>
      <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />
    </Svg>
  )
}

export function IconFilter(p: IconProps) {
  // Sliders — filter affordance.
  return (
    <Svg {...p}>
      <path d="M4 7.5h9M17 7.5h3" />
      <circle cx="15" cy="7.5" r="2" />
      <path d="M4 16.5h3M11 16.5h9" />
      <circle cx="9" cy="16.5" r="2" />
    </Svg>
  )
}

export function IconHeart(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 20.2S4 15.1 4 9.6C4 6.9 6.1 5 8.4 5c1.5 0 2.8.8 3.6 2 .8-1.2 2.1-2 3.6-2C17.9 5 20 6.9 20 9.6c0 5.5-8 10.6-8 10.6Z" />
    </Svg>
  )
}

export function IconShare(p: IconProps) {
  // iOS-style share: box with arrow up.
  return (
    <Svg {...p}>
      <path d="M12 14.5V3.8" />
      <path d="m8.5 6.8 3.5-3.4 3.5 3.4" />
      <path d="M7.5 10.5H6a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5h-1.5" />
    </Svg>
  )
}

export function IconClose(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 2}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Svg>
  )
}

export function IconXCircle(p: IconProps) {
  // Reject affordance — X inside a circle (seller decline, cancel).
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" />
    </Svg>
  )
}

export function IconCheck(p: IconProps) {
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 2}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  )
}

export function IconCamera360(p: IconProps) {
  // 360 / media viewer — play badge inside a rounded frame.
  return (
    <Svg {...p}>
      <rect x="3.5" y="5" width="17" height="14" rx="3" />
      <path d="m10.3 9.3 4.6 2.7-4.6 2.7V9.3Z" />
    </Svg>
  )
}

export function IconBell(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 4a5.4 5.4 0 0 0-5.4 5.4c0 4.4-1.6 5.7-1.6 5.7h14s-1.6-1.3-1.6-5.7A5.4 5.4 0 0 0 12 4Z" />
      <path d="M10.2 18.6a2 2 0 0 0 3.6 0" />
    </Svg>
  )
}

export function IconUpload(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 15V4.5" />
      <path d="m8.2 7.8 3.8-3.7 3.8 3.7" />
      <path d="M4.5 15.5v3A1.5 1.5 0 0 0 6 20h12a1.5 1.5 0 0 0 1.5-1.5v-3" />
    </Svg>
  )
}

export function IconCard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M3 9.8h18" />
      <path d="M6.5 14.8h4" />
    </Svg>
  )
}

export function IconShield(p: IconProps) {
  // KYC / verified.
  return (
    <Svg {...p}>
      <path d="M12 3.5 5 6.2v5.2c0 4.4 3 7.6 7 9.1 4-1.5 7-4.7 7-9.1V6.2L12 3.5Z" />
      <path d="m9 11.8 2.2 2.2 3.8-4" />
    </Svg>
  )
}

export function IconMic(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </Svg>
  )
}

export function IconPlus(p: IconProps) {
  // Add-to-cart affordance on stone cards.
  return (
    <Svg {...p} strokeWidth={p.strokeWidth ?? 2.2}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  )
}

export function IconBag(p: IconProps) {
  // Shopping bag — the cart surface.
  return (
    <Svg {...p}>
      <path d="M5.6 8h12.8a.7.7 0 0 1 .7.76l-.87 10.4a1.6 1.6 0 0 1-1.6 1.47H7.37a1.6 1.6 0 0 1-1.6-1.47L4.9 8.76A.7.7 0 0 1 5.6 8Z" />
      <path d="M9 10.6V6.7a3 3 0 0 1 6 0v3.9" />
    </Svg>
  )
}

export function IconSend(p: IconProps) {
  // Up-arrow send, ChatGPT-composer style.
  return (
    <Svg {...p}>
      <path d="M12 19V5" />
      <path d="m5.5 11.5 6.5-6.5 6.5 6.5" />
    </Svg>
  )
}

export function IconSparkle(p: IconProps) {
  // Four-point concave sparkle — the natural-language / "smart search" glint.
  // Filled (renders as the gold accent inside the search pill).
  return (
    <Svg {...p} filled>
      <path d="M12 2c.6 5.5 4.5 9.4 10 10-5.5.6-9.4 4.5-10 10-.6-5.5-4.5-9.4-10-10 5.5-.6 9.4-4.5 10-10Z" />
    </Svg>
  )
}
